import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import WelcomeCard from './components/WelcomeCard'
import ScrollWelcome from './components/ScrollWelcome'
import ThankYouScreen from './components/ThankYouScreen'
import TrustLockup from './components/TrustLockup'
import DebugOverlay from './components/DebugOverlay'
import FormShell from './components/form/FormShell'
import InviteOnly from './components/InviteOnly'
import { useOnboardingStore } from './stores/onboardingStore'
import PreviewPage from './pages/PreviewPage'

// Bare-domain gate: the form may ONLY load when a unique invite link (?p=<slug>)
// is present. No slug → invite-only dead-end (no welcome, no Begin, no form).
// A present slug always loads the form; the backend governs token validity.
const hasInvite =
  typeof window !== 'undefined' &&
  !!new URLSearchParams(window.location.search).get('p')?.trim()

const reduced =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function WelcomePage({ onBegin, hasSavedState }) {
  const bgRef = useRef(null)
  const cursorOffset = useRef({ x: 0, y: 0 })

  // Branded loader: show instantly (inline SVG), preload hero.png, then fade in.
  // If the image is cached (<300ms) we skip the loader entirely — no flash.
  const [heroReady, setHeroReady] = useState(false)
  const [showLoader, setShowLoader] = useState(true)
  const loadStart = useRef(Date.now())

  useEffect(() => {
    const img = new Image()
    img.src = '/hero.png'

    const onLoad = () => {
      const elapsed = Date.now() - loadStart.current
      if (elapsed < 300) {
        setHeroReady(true)
        setShowLoader(false)
      } else {
        setHeroReady(true)
        setTimeout(() => setShowLoader(false), 400)
      }
    }

    if (img.complete) {
      onLoad()
    } else {
      img.onload = onLoad
      img.onerror = onLoad
    }
  }, [])

  useEffect(() => {
    const page = bgRef.current
    if (!page || reduced) return

    const startTime = Date.now()
    let rafId

    // Cursor parallax — feeds into the rAF loop, doesn't set position directly
    const handleMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      cursorOffset.current = { x: -x * 7, y: -y * 5 }
    }

    // Combined loop: slow idle figure-8 drift + cursor offset, additive
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000
      const idleX = Math.sin(elapsed * 0.31) * 4
      const idleY = Math.cos(elapsed * 0.23) * 2.5
      const totalX = idleX + cursorOffset.current.x
      const totalY = idleY + cursorOffset.current.y
      page.style.backgroundPosition = `calc(50% + ${totalX}px) calc(60% + ${totalY}px)`
      rafId = requestAnimationFrame(tick)
    }

    tick()
    window.addEventListener('mousemove', handleMove)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('mousemove', handleMove)
    }
  }, [])

  const handleTap = (e) => {
    const t = e.target
    if (
      t.closest?.('[data-no-tap]') ||
      t.closest?.('a, input, textarea, select')
    )
      return
    onBegin()
  }

  useEffect(() => {
    const isTyping = () => {
      const a = document.activeElement
      return (
        !!a &&
        (a.tagName === 'INPUT' ||
          a.tagName === 'TEXTAREA' ||
          a.isContentEditable)
      )
    }
    const onKey = (e) => {
      if (isTyping()) return
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault()
        onBegin()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBegin])

  return (
    <div
      ref={bgRef}
      className={`page page--hero ${heroReady ? 'hero-loaded' : 'hero-loading'}`}
      onClick={handleTap}
      role="presentation"
    >
      {showLoader && (
        <div className={`hero-loader ${heroReady ? 'hero-loader--out' : ''}`}>
          <svg
            className="hero-loader-logo"
            viewBox="240 445 112 112"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="#45BDA6">
              <g>
                <path d="M272.2,472.4c0,2.9-2.4,5.3-5.3,5.3c-2.9,0-5.3-2.4-5.4-5.3c0-2.9,2.4-5.3,5.3-5.3C269.8,467.1,272.2,469.5,272.2,472.4z" />
                <path d="M318.2,529c0-2.9,2.4-5.3,5.3-5.3c3,0,5.4,2.4,5.4,5.4c0,2.9-2.4,5.3-5.4,5.3C320.6,534.4,318.2,532,318.2,529z" />
                <path d="M310.6,469.1c-3,0-5.4-2.4-5.4-5.4c0-2.9,2.4-5.3,5.3-5.3c2.9,0,5.3,2.4,5.3,5.3C315.9,466.7,313.5,469.1,310.6,469.1z" />
                <path d="M323.5,477.8c-2.5,0-5.4-2.3-5.3-5.4c0.1-2.9,2.4-5.3,5.4-5.3c2.9,0,5.3,2.4,5.3,5.4C328.9,475.4,326.5,477.8,323.5,477.8z" />
                <path d="M332.2,521.4c-3,0-5.3-2.4-5.3-5.4c0-3,2.4-5.3,5.4-5.3c3,0.1,5.3,2.5,5.3,5.4C337.5,519.1,335.1,521.4,332.2,521.4z" />
                <path d="M263.6,516.1c0,2.9-2.4,5.3-5.3,5.3c-2.9,0-5.3-2.4-5.3-5.3c0-2.9,2.4-5.3,5.3-5.3C261.2,510.7,263.5,513.1,263.6,516.1z" />
                <path d="M310.6,532.4c3,0,5.3,2.4,5.3,5.4c0,2.9-2.5,5.3-5.4,5.3c-2.9-0.1-5.3-2.4-5.3-5.3C305.2,534.7,307.6,532.4,310.6,532.4z" />
                <path d="M279.8,469.1c-2.9,0-5.3-2.4-5.3-5.4c0-2.9,2.5-5.3,5.4-5.3c2.9,0,5.3,2.5,5.3,5.3C285.2,466.7,282.8,469.1,279.8,469.1z" />
                <path d="M258.3,480.1c2.9,0,5.3,2.4,5.3,5.4c0,2.9-2.4,5.3-5.4,5.3c-2.9,0-5.3-2.5-5.3-5.4C252.9,482.4,255.3,480.1,258.3,480.1z" />
                <path d="M261.7,529.2c-0.2-3.7,2.9-5.5,5.2-5.5c2.9,0,5.3,2.4,5.3,5.3c0,2.9-2.4,5.3-5.3,5.3C264,534.4,261.7,532.1,261.7,529.2z" />
                <path d="M255.2,506.1c-3,0-5.3-2.4-5.3-5.3c0-3,2.4-5.3,5.3-5.3c3,0,5.3,2.3,5.3,5.3C260.5,503.7,258.1,506.1,255.2,506.1z" />
                <path d="M340.6,500.8c0,3-2.4,5.3-5.4,5.3c-2.9,0-5.3-2.3-5.3-5.3c0-2.9,2.3-5.3,5.3-5.4C338.3,495.4,340.6,497.7,340.6,500.8z" />
                <path d="M326.9,485.4c0-3,2.4-5.3,5.3-5.3c3,0,5.3,2.4,5.3,5.3c0,2.9-2.4,5.4-5.3,5.3C329.3,490.7,326.9,488.4,326.9,485.4z" />
                <path d="M280,532.4c2.9,0,5.3,2.5,5.3,5.4c0,2.9-2.5,5.3-5.4,5.3c-3,0-5.3-2.4-5.3-5.4C274.6,534.7,277,532.4,280,532.4z" />
                <path d="M300.5,460.7c0,3-2.4,5.3-5.3,5.3c-3,0-5.3-2.4-5.3-5.3c0-3,2.4-5.3,5.4-5.3C298.2,455.4,300.6,457.8,300.5,460.7z" />
                <path d="M300.5,540.7c0,3-2.3,5.3-5.4,5.3c-2.9,0-5.2-2.3-5.3-5.3c0-3,2.3-5.3,5.3-5.3C298.2,535.4,300.5,537.8,300.5,540.7z" />
              </g>
              <g>
                <path d="M294.9,530c-16.7-0.3-29.2-13.5-28.9-30.5c0.3-15.6,14-28.4,29.9-28.1c16.2,0.3,28.8,13.8,28.5,30.4C324.1,517.3,310.6,530.3,294.9,530z M291.6,497.7c-2.7-0.4-4.3,0.9-5.2,3.1c-1.3,3,0.1,6.7,3.2,8.7c5.8,3.7,14.2-0.7,15.2-7.9c1.4-10.1-8.4-18-18.4-14.9c-6.9,2.2-11.4,6.9-12.5,14.1c-1.2,8.1,1.7,14.7,8.4,19.4c9.9,6.9,23.7,4.6,31.2-4.9c7.6-9.6,6.5-23.4-2.6-31.7c-8.4-7.7-22.1-8.2-30-0.8c1.3-0.5,2.1-0.8,2.9-1.1c5.7-2,11.3-1.7,16.7,1.2c6.9,3.6,11.3,12,10.1,19c-1.4,8.1-6.7,13.6-14.7,14.5c-5.2,0.6-9.6-1.2-12.8-5.5c-2.9-3.9-3.6-8.3-1.5-12.9c1.9-4,6.1-6.4,10.4-5.9c3.7,0.4,6.5,2.9,7.2,6.5c0.5,2.7-0.3,5-2.8,6.3c-2,1-4.2,1.1-5.7-0.8C288.9,501.8,289.9,499.7,291.6,497.7z" />
                <path d="M295.2,556c-30.5,0-55.2-24.8-55.2-55.2s24.8-55.2,55.2-55.2c30.5,0,55.2,24.8,55.2,55.2S325.7,556,295.2,556z M295.2,450.3c-27.8,0-50.4,22.6-50.4,50.4c0,27.8,22.6,50.4,50.4,50.4c27.8,0,50.4-22.6,50.4-50.4C345.6,472.9,323,450.3,295.2,450.3z" />
              </g>
            </g>
          </svg>
        </div>
      )}
      <main className="main-center" style={{ opacity: heroReady ? 1 : 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
          data-no-tap={hasSavedState ? 'true' : undefined}
        >
          <WelcomeCard onBegin={onBegin} />
        </div>
      </main>
      <footer
        className="bottom-bar arrival arrival-bottom"
        style={{ opacity: heroReady ? 1 : 0 }}
      >
        <div className="bb-left" aria-hidden="true" />
        <div className="bb-center">
          <TrustLockup />
        </div>
        <div className="bb-right" aria-hidden="true" />
      </footer>
    </div>
  )
}

function OnboardingApp() {
  const hasEntered = useOnboardingStore((s) => s.hasEntered)
  const isComplete = useOnboardingStore((s) => s.isComplete)
  const startFlow = useOnboardingStore((s) => s.startFlow)
  const resumeFlow = useOnboardingStore((s) => s.resumeFlow)
  const reset = useOnboardingStore((s) => s.reset)
  const setPersonalization = useOnboardingStore((s) => s.setPersonalization)
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const hasSavedState = useOnboardingStore(
    (s) => s.currentStep > 1 || Object.keys(s.formData).length > 0,
  )

  // Fetch personalization once per session if ?p=<slug> present. Lives in the
  // store so WelcomeCard, Step5 (auto-apply coupon), and any other step can
  // read it without prop drilling. Fires before the form is entered so Step5
  // sees it immediately when reached.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const slug = new URLSearchParams(window.location.search).get('p')
    if (!slug) return
    let cancelled = false
    // Personalization is the ONLY read served by the briteOS backend; the new
    // endpoint returns the practice fields directly (no { ok, data } wrapper)
    // and uses different field names than the consumers expect, so map them.
    const osBase = import.meta.env.VITE_ONBOARDING_OS_API_URL || ''
    fetch(`${osBase}/personalization?slug=${encodeURIComponent(slug)}`, {
      credentials: 'omit',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || typeof d !== 'object') return
        const BUCKET_MAP = {
          174: 'standard',
          99: 'small_practice',
          waive_6mo: 'trial_then_174',
          '99_after_6mo_waive': 'trial_then_99',
        }
        setPersonalization({
          ...d,
          // WelcomeCard renders the personal note as `note`.
          note: d.welcomeNote ?? '',
          // Step2 PlatformFeeCard switches on a discountBucket vocabulary.
          discountBucket: BUCKET_MAP[d.providerFeeBucket] ?? 'standard',
          // Step3 managed-billing fee override reads stripeProcessingFeePct.
          stripeProcessingFeePct:
            typeof d.managedBillingFeePct === 'number' ? d.managedBillingFeePct : null,
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [setPersonalization])

  // Debug-only persona seeding: ?persona=kashif loads hardcoded test data
  // into the personalization slice without hitting the backend. Used to
  // preview personalized welcome / Step 2 / Step 5 copy in dev sessions.
  // Production personalization always uses ?p=<slug> above.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const persona = new URLSearchParams(window.location.search).get('persona')
    if (!persona) return
    const PERSONAS = {
      kashif: {
        practiceName: 'Docther',
        ownerFirstName: 'Kashif',
        welcomeLine: "Excited to have you on, Kashif. Text me if anything looks off.",
        note: 'INTERNAL: Kashif onboarding — DTC + N+ + Managed Billing',
        discountBucket: 'trial_then_99',
        hormoneDiscountPct: null,
        nplusPatientFeeDiscountPct: null,
        couponCode: null,
        stripeProcessingFeePct: null,
      },
    }
    const data = PERSONAS[persona]
    if (data) setPersonalization(data)
  }, [setPersonalization])

  const handleStartOver = () => {
    reset()
    startFlow()
  }

  // Coupon eligibility gate — set localStorage flag from ?coupon=eligible once.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const c = new URLSearchParams(window.location.search).get('coupon')
      if (c === 'eligible') {
        localStorage.setItem('brite-coupon-eligible', 'true')
      }
    } catch {}
  }, [])

  // Debug overlay gate — any presence of ?debug (e.g. ?debug, ?debug=1,
  // ?p=slug&debug) persists to sessionStorage; ?debug=0 clears it.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.has('debug')) {
        if (params.get('debug') === '0') sessionStorage.removeItem('brite-debug')
        else sessionStorage.setItem('brite-debug', '1')
      }
    } catch {}
  }, [])

  const debugEnabled =
    typeof window !== 'undefined' &&
    (() => {
      const params = new URLSearchParams(window.location.search)
      if (params.has('debug')) return params.get('debug') !== '0'
      return sessionStorage.getItem('brite-debug') === '1'
    })()

  // DEV keybind: R resets the whole flow back to welcome
  useEffect(() => {
    const isTyping = () => {
      const a = document.activeElement
      return (
        !!a &&
        (a.tagName === 'INPUT' ||
          a.tagName === 'TEXTAREA' ||
          a.isContentEditable)
      )
    }
    const onKey = (e) => {
      if (isTyping()) return
      if (e.key?.toLowerCase?.() === 'r') {
        e.preventDefault()
        reset()
        console.log('[dev] flow reset')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [reset])

  const fade = reduced
    ? { duration: 0 }
    : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }

  return (
    <>
      <AnimatePresence mode="wait">
        {isComplete ? (
          <motion.div
            key="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          >
            <ThankYouScreen />
          </motion.div>
        ) : hasEntered ? (
          <motion.div
            key="shell"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          >
            <FormShell />
          </motion.div>
        ) : (
          <motion.div
            key="welcome"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          >
            {liteMode || reduced ? (
              <WelcomePage
                onBegin={startFlow}
                hasSavedState={hasSavedState}
                onResume={resumeFlow}
                onStartOver={handleStartOver}
              />
            ) : (
              <ScrollWelcome
                onBegin={startFlow}
                hasSavedState={hasSavedState}
                onResume={resumeFlow}
                onStartOver={handleStartOver}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {debugEnabled && <DebugOverlay />}
    </>
  )
}

export default function App() {
  const path =
    typeof window !== 'undefined' ? window.location.pathname : ''

  if (path.startsWith('/preview/') || path === '/preview') {
    return <PreviewPage />
  }

  // Invite-only gate: bare domain (no ?p= slug) never loads the form — not even
  // a saved/resumable draft. A present slug falls through to normal behavior.
  if (!hasInvite) {
    return <InviteOnly />
  }

  return <OnboardingApp />
}
